import { useState } from 'react'
import { message, Modal } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import type { Trial } from '../types/trial.types'
import { trialsService } from '../services/trials.service'

export const useTrialActions = () => {
    const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false)
    const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false)
    const [extensionTrial, setExtensionTrial] = useState<Trial | null>(null)
    const queryClient = useQueryClient()

    const handleAssignTrial = () => {
        setIsAssignmentModalOpen(true)
    }

    const handleExtendTrial = (trial: Trial) => {
        setExtensionTrial(trial)
        setIsExtensionModalOpen(true)
    }

    const handleRevokeTrial = (trial: Trial) => {
        Modal.confirm({
            title: 'Revoke Trial',
            content: `Are you sure you want to revoke the trial for ${trial.merchantName}? This will immediately end their access.`,
            okText: 'Revoke Access',
            okType: 'danger',
            onOk: async () => {
                try {
                    await trialsService.revokeTrial(trial.merchantId, 'Admin revoked access')
                    message.success('Trial revoked successfully!')
                    queryClient.invalidateQueries({ queryKey: ['trials'] })
                } catch (error) {
                    message.error('Failed to revoke trial')
                }
            },
        })
    }

    const handleAssignmentSubmit = async (values: any) => {
        try {
            await trialsService.assignTrial({
                merchant_id: values.merchantId,
                duration_days: values.duration,
                notes: values.notes,
                trial_bidding_radius_km: values.trial_bidding_radius_km,
                trial_visibility_radius_km: values.trial_visibility_radius_km,
            })
            message.success(`Trial assigned to merchant ${values.merchantId}!`)
            setIsAssignmentModalOpen(false)
            queryClient.invalidateQueries({ queryKey: ['trials'] })
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to assign trial')
        }
    }

    const handleExtensionSubmit = async (values: { duration: number }) => {
        if (!extensionTrial) return

        try {
            await trialsService.extendTrial(extensionTrial.merchantId, values.duration)
            message.success(`Trial extended for ${extensionTrial.merchantName}!`)
            setIsExtensionModalOpen(false)
            setExtensionTrial(null)
            queryClient.invalidateQueries({ queryKey: ['trials'] })
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to extend trial')
        }
    }

    const closeAssignmentModal = () => setIsAssignmentModalOpen(false)
    const closeExtensionModal = () => {
        setIsExtensionModalOpen(false)
        setExtensionTrial(null)
    }

    return {
        isAssignmentModalOpen,
        handleAssignTrial,
        handleExtendTrial,
        handleRevokeTrial,
        handleAssignmentSubmit,
        closeAssignmentModal,
        // Extension props
        isExtensionModalOpen,
        extensionTrial,
        handleExtensionSubmit,
        closeExtensionModal
    }
}
